import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { Display, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { PADDING, POSITION } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, setHTMLElementAttributes, setHTMLElementContent, setHtmlElementOptions, toggleAttribute } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { isNumber } from "@cervval.js/common/src/tools/number.tools";
import { MILLIS_IN_ONE_SECOND } from "@cervval.js/common/src/tools/time.tools";
import { UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { NOW } from "@cervval.js/common/src/tools/timestamps.tools.factory";
import { diffRound } from "@cervval.js/common/src/tools/timestamps.tools.tsdate";
import { MILLISECONDS } from "@cervval.js/common/src/tools/type.tools";
import { CjsButton } from "../button/button";
import { I18nType, i18n } from "./i18n/i18n";


export const TOAST_Z_INDEX = "2000";

/** 
 * Bootstrap Toast styles available
 * @see https://getbootstrap.com/docs/5.3/helpers/color-background/
 */
export enum ToastStyle {
    NONE = "text-bg-none",
    PRIMARY = "text-bg-primary",
    SECONDARY = "text-bg-secondary",
    SUCCESS = "text-bg-success",
    DANGER = "text-bg-danger",
    WARNING = "text-bg-warning",
    INFO = "text-bg-info",
    LIGHT = "text-bg-light",
    DARK = "text-bg-dark",
    LINK = "text-bg-link"
}


/** 
 * Bootstrap Toast positions available
 * @see https://getbootstrap.com/docs/5.0/components/toasts/#placement
 */
export enum ToastPlacement {
    TOP_LEFT = "top-0 start-0",
    TOP_CENTER = "top-0 start-50 translate-middle-x",
    TOP_RIGHT = "top-0 end-0",
    MIDDLE_LEFT = "top-50 start-0 translate-middle-y",
    MIDDLE_CENTER = "top-50 start-50 translate-middle",
    MIDDLE_RIGHT = "top-50 end-0 translate-middle-y",
    BOTTOM_LEFT = "bottom-0 start-0",
    BOTTOM_CENTER = "bottom-0 start-50 translate-middle-x",
    BOTTOM_RIGHT = "bottom-0 end-0"
}

interface TimeElapsedContext {
    /** Start ts of interval */
    startTs: UnixTimestamp;
    /** Id of interval, useful to remove it */
    intervalId?: number;
}

/** Parameters for Toast component */
export interface ToastOptions {

    /** Title of toast, appended in header */
    title: AcceptedHtmlElementContent;

    /** Content of toast, appended in body */
    content: AcceptedHtmlElementContent;

    /** Display dismiss button (xmark icon) */
    enableDismiss: boolean;

    /** Delay hiding the toast (ms) */
    delay: MILLISECONDS | undefined;

    /** Auto hide the toast */
    autohide: boolean;

    /** Apply a CSS fade transition to the toast */
    animation: boolean;

    /** 
     * Display a load bar under toast 
     * TODO
     */
    enableDismissLoadBar?: boolean;

    /**
     * Toast style (Bootstrap color and background)
     */
    cssStyle: ToastStyle;

    /** Placement of Toast in app screen */
    placement?: ToastPlacement;

    /** Close on click */
    tapToDismiss: boolean;

    /** 
     * Display time left before popup close in time div. 
     * Requires a delay to be passed.
     */
    timeElapsed?: boolean;

    /** Hide toast sections */
    hideSections: ("header" | "body")[];
}

/** Parameters for Toast component */
export const defaultToastOptions: ToastOptions = {

    /** Title of toast, append in header */
    title: "",

    /** Content of toast, append in body */
    content: "",

    /**
     * Toast style (Bootstrap color and background)
     */
    cssStyle: ToastStyle.PRIMARY,

    /** Delay hiding the toast (ms) */
    delay: 3000,

    /** Auto hide the toast */
    autohide: false,

    /** Apply a CSS fade transition to the toast */
    animation: false,

    /** Display dismiss button (xmark icon) */
    enableDismiss: false,

    /** Hide toast sections */
    hideSections: [],

    /** 
     * Display a load bar under toast 
     * TODO
     */
    enableDismissLoadBar: false,

    /** Close on click */
    tapToDismiss: true

};

/** Export tag of cjs-toast */
export const toastTagName = "cjs-toast";

@CustomElement({
    name: toastTagName,
    template: require("./toast.html"),
    style: require("./toast.scss"),
    elementCssClasses: [PADDING.P_2],
    i18n
})
export class CjsToast extends WebComponent<I18nType> {

    @TypedWebComponentOptions(defaultToastOptions)
    public readonly options!: WebComponentOptions<ToastOptions>;

    @TypedHtmlElements()
    public readonly components!: {
        toastContainer: HTMLDivElement,
        toastHeader: HTMLDivElement,
        toastTitle: HTMLDivElement,
        toastTime: HTMLDivElement,
        dismissButton: CjsButton,
        toastBody: HTMLDivElement
    };

    /**
     * This event fires immediately when the show instance method is called.
     * Linked to: show.bs.toast
     */
    @Event(NoBubblesEventInit)
    public showEvent!: EventHandler;

    /**
     * This event is fired when the toast has been made visible to the user.
     * Linked to: shown.bs.toast
     */
    @Event(NoBubblesEventInit)
    public shownEvent!: EventHandler;

    /**
     * This event is fired immediately when the hide instance method has been called.
     * Linked to: hide.bs.toast
     */
    @Event(NoBubblesEventInit)
    public hideEvent!: EventHandler;

    /**
     * This event is fired when the toast has finished being hidden from the user.
     * Linked to: hidden.bs.toast
     */
    @Event(NoBubblesEventInit)
    public hiddenEvent!: EventHandler;

    /**
     * Store context of time elapsed
     * Defined only if a timeElapsed info is displayed
     */
    private _timeElapsedContext?: TimeElapsedContext;

    /** @returns unique toast id */
    public get toastId(): number {
        return this.componentNumber;
    }

    constructor(options?: Partial<ToastOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }


    /** Called when toast component is init */
    public override init(): void {

        // Set id attribute
        this.setAttribute("id", String(this.toastId));

        // Set attributes for close button
        setHtmlElementOptions(this.components.dismissButton.components.button, {
            cssClass: ["btn-close"],
            attributes: {
                "data-bs-dismiss": "toast",
                "aria-label": this.i18n.closeButton.label
            }
        });

        this._initListeners();

    }

    /** When component refresh */
    public override refresh(): void {

        this._refreshDismissButton();

        this._refreshTitle();
        this._refreshContent();
        this._refreshStyle();
        this._refreshHideSections();
        this._refreshTapToDismiss();

        setHTMLElementAttributes(this.components.toastContainer, {
            "data-bs-autohide": String(this.options.autohide),
            "data-bs-animation": String(this.options.animation)
        });

        // Set delay attribute on toast only if delay is a not null number value. Empty string value is not handled by bootstrap
        toggleAttribute(this.components.toastContainer, isNumber(this.options.delay), "data-bs-delay", String(this.options.delay));
    }

    /**
     * Show toast
     *
     * @param p
     * @param p.fromService if true, toast is managed and append to dom with toastService
     */
    public show(p?: { fromService: boolean }): void {
        this.showEvent.emit();

        // Append toast to body if toast isn't managed with toastService
        if (!p?.fromService) {

            this.refresh();
            this._refreshPlacement(p);
            document.body.append(this);
        }

        this.components.toastContainer.classList.remove("hide");
        this.components.toastContainer.classList.add("show");
        if (this.options.animation) {
            this.components.toastContainer.classList.add("fade");
        }

        if (this.options.delay) {
            setTimeout(() => {// Hide toast after given delay
                this.hide();
            }, this.options.delay);
        } else if (this.options.autohide) {
            setTimeout(() => {// If autohide, hide after default delay value
                this.hide();
            }, defaultToastOptions.delay);
        }

        this._showTimeElapsed();

        this.shownEvent.emit();
    }

    /** Hide toast */
    public hide(): void {
        this.hideEvent.emit();
        this.components.toastContainer.classList.remove("show");
        this.components.toastContainer.classList.add("hide");
        this.remove();
        this._clearElapsedIntervalIfNeeded(this._timeElapsedContext);
        this.hiddenEvent.emit();
    }

    /** Init toast listeners */
    private _initListeners(): void {
        this.addEventListener("click", () => {
            if (!this.options.tapToDismiss) { return; }
            this.remove();
            this.hideEvent.emit();
            this.hiddenEvent.emit();
        });
    }

    /** Refresh state of dismiss button */
    private _refreshDismissButton(): void {
        toggleVisible(this.components.dismissButton, this.options.enableDismiss, Display.BLOCK);
    }

    /** Refresh toast title */
    private _refreshTitle(): void {
        setHTMLElementContent(this.components.toastTitle, this.options.title);
    }

    /** Refresh toast content */
    private _refreshContent(): void {
        setHTMLElementContent(this.components.toastBody, this.options.content);
    }

    /** Refresh toast style */
    private _refreshStyle(): void {
        this.components.toastHeader.classList.add(this.options.cssStyle);
    }


    /** Refresh hide sections */
    private _refreshHideSections(): void {
        toggleVisible(this.components.toastHeader, !this.options.hideSections.includes("header"), Display.FLEX);
        toggleVisible(this.components.toastBody, !this.options.hideSections.includes("body"), Display.FLEX);
    }

    /** Refresh toast placement */
    private _refreshPlacement(p?: { fromService: boolean }): void {
        if (p?.fromService) { return; }
        if (!this.options.placement) {
            this.options.setWithoutRefresh({ placement: ToastPlacement.BOTTOM_RIGHT });
        }
        this.classList.add(...this.options.placement!.split(" "), POSITION.ABSOLUTE);
    }

    /** Refresh toast timeElapsed */
    private _showTimeElapsed(): void {
        if (!this.options.timeElapsed || this.options.delay == null) { return; }

        const secondDelay = this.options.delay / MILLIS_IN_ONE_SECOND;
        const updateTimerText = (context: TimeElapsedContext): void => {
            const start = context?.startTs;
            if (this.options.delay == null || start == null) { return; }
            const timerDiff = diffRound(NOW(), start, "second");
            this.components.toastTime.innerText = `${timerDiff + secondDelay}s`;
        };

        // Clear already existing interval
        if (this._timeElapsedContext) {
            this._clearElapsedIntervalIfNeeded(this._timeElapsedContext);
        }

        const context: TimeElapsedContext = { startTs: NOW() };

        updateTimerText(context);

        context.intervalId = setInterval(() => {
            updateTimerText(context);
            this._clearElapsedIntervalIfNeeded(context);
        }, MILLIS_IN_ONE_SECOND);// 1s
        this._timeElapsedContext = context;
    }

    /** Clear elapsed interval if needed */
    private _clearElapsedIntervalIfNeeded(context: TimeElapsedContext | undefined): void {
        if (context == null) { return; }

        const start = context?.startTs;
        const intervalId = context?.intervalId;
        if (start == null || this.options.delay == null || intervalId == null) { return; }
        const timerDiff = diffRound(start, NOW(), "second");
        const secondDelay = this.options.delay / MILLIS_IN_ONE_SECOND;
        if (timerDiff >= secondDelay) {
            clearInterval(intervalId);
            this._timeElapsedContext = undefined;
        }
    }


    /** Refresh toast tapToDismiss */
    private _refreshTapToDismiss(): void {
        if (this.options.tapToDismiss) {
            this.style.cursor = "pointer";
        } else {
            this.style.cursor = "default";
        }
    }
}